import { QueryClient } from '@tanstack/react-query';
import { Folder } from './folders_api';

// フォルダのミューテーションはすべて自分（オーナー）のライブラリ上でのみ発生するため、
// キャッシュキーは常に ['folders', 'me'] で固定（公開プロフィールは読み取り専用）。
export const FOLDERS_QUERY_KEY = ['folders', 'me'] as const;

type FolderList = Folder[] | undefined;

/**
 * フォルダ一覧の React Query キャッシュを、サーバー確定値で直接更新するヘルパー群。
 * これにより各操作後の全件再 fetch（refetch）を省く。
 * 失敗時は invalidate でサーバーから取り直す前提。
 */
export const folderCache = {
  /** 新規フォルダを末尾に追加（order_num は作成時に最大+1 が振られているため末尾でよい）。 */
  add(qc: QueryClient, folder: Folder) {
    qc.setQueryData<FolderList>(FOLDERS_QUERY_KEY, (old) => [...(old || []), folder]);
  },

  /** 既存フォルダを更新後オブジェクトで置換。 */
  update(qc: QueryClient, folder: Folder) {
    qc.setQueryData<FolderList>(FOLDERS_QUERY_KEY, (old) =>
      (old || []).map((f) => (f.id === folder.id ? folder : f)),
    );
  },

  /** フォルダを除去。 */
  remove(qc: QueryClient, id: number) {
    qc.setQueryData<FolderList>(FOLDERS_QUERY_KEY, (old) =>
      (old || []).filter((f) => f.id !== id),
    );
  },

  /** 並び替え結果の配列でキャッシュを置換（order_num も新しい index に振り直す）。 */
  reorder(qc: QueryClient, folders: Folder[]) {
    qc.setQueryData<FolderList>(
      FOLDERS_QUERY_KEY,
      folders.map((f, i) => ({ ...f, order_num: i })),
    );
  },

  /** 1フォルダの item_count を更新。 */
  setCount(qc: QueryClient, folderId: number, count: number) {
    qc.setQueryData<FolderList>(FOLDERS_QUERY_KEY, (old) =>
      (old || []).map((f) => (f.id === folderId ? { ...f, item_count: count } : f)),
    );
  },

  /** 複数フォルダの item_count をまとめて更新（{folderId: count}）。 */
  setCounts(qc: QueryClient, counts: Record<number, number>) {
    qc.setQueryData<FolderList>(FOLDERS_QUERY_KEY, (old) =>
      (old || []).map((f) =>
        counts[f.id] !== undefined ? { ...f, item_count: counts[f.id] } : f,
      ),
    );
  },

  /** ピン留めフォルダの並び順（pin_order）を id 配列の順序で振り直す。 */
  applyPinOrder(qc: QueryClient, orderedIds: number[]) {
    qc.setQueryData<FolderList>(FOLDERS_QUERY_KEY, (old) =>
      (old || []).map((f) => {
        const idx = orderedIds.indexOf(f.id);
        return idx >= 0 ? { ...f, pin_order: idx } : f;
      }),
    );
  },

  /** 失敗時のフォールバック：サーバーから取り直す。 */
  invalidate(qc: QueryClient) {
    qc.invalidateQueries({ queryKey: FOLDERS_QUERY_KEY });
  },
};
